import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../contexts/CompanyContext';
import { companiesApi } from '../api/companies';
import { categoriesApi } from '../api/categories';
import { teamsApi } from '../api/teams';
import { INDUSTRY_OPTIONS } from '../lib/industryOptions';
import toast from 'react-hot-toast';
import { Plus, Users, Tag, Clock, Zap, Layers, RefreshCw, Bot, Eye, EyeOff, Globe, KeyRound, Mail, CircleDot, FolderGit2, ListChecks, SlidersHorizontal, Inbox } from 'lucide-react';
import type { Category, Team } from '../types';
import SLAPoliciesTab from '../components/settings/SLAPoliciesTab';
import TicketStatusesTab from '../components/settings/TicketStatusesTab';
import ClassificationsTab from '../components/settings/ClassificationsTab';
import AutoAssignTab from '../components/settings/AutoAssignTab';
import TeamEditorModal from '../components/settings/TeamEditorModal';
import MembersTab from '../components/settings/MembersTab';
import PortalSettingsTab from '../components/settings/PortalSettingsTab';
import ApiKeysTab from '../components/settings/ApiKeysTab';
import EmailTab from '../components/settings/EmailTab';
import ProductLinesTab from '../components/settings/ProductLinesTab';
import TaskTemplatesTab from '../components/settings/TaskTemplatesTab';
import ClaudeDispatchTab from '../components/settings/ClaudeDispatchTab';
import CrmSettingsTab from '../components/settings/CrmSettingsTab';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';

const TAB_IDS = new Set(['general', 'statuses', 'classification', 'categories', 'teams', 'sla', 'auto-assign', 'members', 'product-lines', 'task-templates', 'crm', 'ai', 'claude-dispatch', 'customer-portal', 'email', 'integrations']);
type TabId = 'general' | 'statuses' | 'classification' | 'categories' | 'teams' | 'sla' | 'auto-assign' | 'members' | 'product-lines' | 'task-templates' | 'crm' | 'ai' | 'claude-dispatch' | 'customer-portal' | 'email' | 'integrations';

// ── Simple single-field "name" modal (replaces native prompt) ─────────────────
function NameModal({
  title,
  label,
  placeholder,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onSubmit(v);
    onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      width={440}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!value.trim()}>Add</button>
        </>
      }
    >
      <div>
        <label className="label">{label}</label>
        <input
          className="input"
          value={value}
          autoFocus
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          placeholder={placeholder}
        />
      </div>
    </Modal>
  );
}

// ── AI provider option metadata ───────────────────────────────────────────────
const AI_PROVIDERS = [
  {
    value: 'openai',
    label: 'OpenAI',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    keyPlaceholder: 'sk-...',
    keyHint: 'Find your key at platform.openai.com/api-keys',
  },
  {
    value: 'anthropic',
    label: 'Anthropic (Claude)',
    // Newest first. Leaving the model unset uses claude-opus-5; pick
    // claude-haiku-4-5 instead for high-volume, low-stakes work.
    models: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5', 'claude-opus-4-8'],
    keyPlaceholder: 'sk-ant-...',
    keyHint: 'Find your key at console.anthropic.com/settings/keys',
  },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function CompanySettings() {
  const { currentCompany } = useCompany();
  const queryClient = useQueryClient();
  // ?tab=crm etc. deep-links a tab (the Inbox links here).
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const t = new URLSearchParams(window.location.search).get('tab') as TabId | null;
    return t && TAB_IDS.has(t) ? t : 'general';
  });
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [applyConfirm, setApplyConfirm] = useState(false);

  // Native-prompt replacements
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);

  // Notification settings (stored in company.settings JSON)
  const [notifyEmails, setNotifyEmails] = useState('');
  const [notifySubmitter, setNotifySubmitter] = useState(true);
  const [emailIncludeSubject, setEmailIncludeSubject] = useState(false);

  // Per-tenant ticket prefix (stored in company.settings.ticketPrefix)
  const [ticketPrefix, setTicketPrefix] = useState('');

  // AI settings state
  const [aiProvider, setAiProvider] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [showKey, setShowKey] = useState(false);

  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.get(companyId),
    enabled: !!currentCompany,
  });

  // Both effects seed the form from the loaded company ONCE. Keying them on the
  // fetched object (or its fields) meant any background refetch reset whatever
  // was being edited back to the stored values mid-keystroke.
  useEffect(() => {
    if (company?.name) setCompanyName(company.name);
    if (company?.industry) setIndustry(company.industry);
  }, [(company as any)?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = (company as any)?.settings as Record<string, any> | undefined;
    if (!s) return;
    setNotifyEmails(Array.isArray(s.notifyNewTicketEmails) ? s.notifyNewTicketEmails.join(', ') : '');
    setNotifySubmitter(s.notifySubmitter !== false);
    setEmailIncludeSubject(s.emailIncludeSubject === true);
    setTicketPrefix(typeof s.ticketPrefix === 'string' ? s.ticketPrefix : '');
  }, [(company as any)?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNotificationsMutation = useMutation({
    mutationFn: () =>
      companiesApi.update(companyId, {
        settings: {
          notifyNewTicketEmails: notifyEmails.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
          notifySubmitter,
          emailIncludeSubject,
        },
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      toast.success('Notification settings saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveTicketPrefixMutation = useMutation({
    mutationFn: () =>
      companiesApi.update(companyId, { settings: { ticketPrefix: ticketPrefix.trim() } } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      toast.success('Ticket prefix saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const prefixValid = ticketPrefix.trim() === '' || /^[A-Za-z0-9]{1,8}$/.test(ticketPrefix.trim());
  const prefixPreview = `${(ticketPrefix.trim() || 'cTKT')}-00042`;

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', companyId],
    queryFn: () => categoriesApi.list(),
    enabled: !!currentCompany && (activeTab === 'categories' || activeTab === 'general'),
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teamsApi.list(),
    enabled: !!currentCompany && (activeTab === 'teams' || activeTab === 'general'),
  });


  const { data: aiSettings } = useQuery({
    queryKey: ['ai-settings', companyId],
    queryFn: () => companiesApi.getAISettings(companyId),
    enabled: !!currentCompany && activeTab === 'ai',
  });

  // Sync fetched AI settings into local form state — once per company, so a
  // refetch can't wipe a provider/model the user is part-way through changing.
  const aiSeededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!aiSettings || aiSeededFor.current === companyId) return;
    aiSeededFor.current = companyId;
    setAiProvider(aiSettings.aiProvider ?? '');
    setAiModel(aiSettings.aiModel ?? '');
    setAiApiKey(''); // never pre-fill the key; server only tells us hasApiKey
  }, [aiSettings, companyId]);

  const updateCompanyMutation = useMutation({
    mutationFn: (data: { name?: string; industry?: string | null }) => companiesApi.update(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Company updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const applyIndustryMutation = useMutation({
    mutationFn: (ind: string) => companiesApi.applyIndustryDefaults(companyId, ind),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', companyId] });
      queryClient.invalidateQueries({ queryKey: ['product-lines', companyId] });
      queryClient.invalidateQueries({ queryKey: ['field-definitions', companyId] });
      setApplyConfirm(false);
      toast.success('Industry defaults applied');
    },
    onError: (e: Error) => { toast.error(e.message); setApplyConfirm(false); },
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) => categoriesApi.create({ name, color: '#0ea5e9' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['categories'] }); toast.success('Category created'); },
  });

  const saveAISettingsMutation = useMutation({
    mutationFn: (data: { aiProvider: string; aiApiKey?: string; aiModel: string }) =>
      companiesApi.updateAISettings(companyId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings', companyId] });
      setAiApiKey('');
      toast.success('AI settings saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'general',       label: 'General',       icon: Users },
    { id: 'statuses',      label: 'Statuses',      icon: CircleDot },
    { id: 'classification', label: 'Classification', icon: SlidersHorizontal },
    { id: 'categories',    label: 'Categories',    icon: Tag },
    { id: 'teams',         label: 'Teams',          icon: Users },
    { id: 'sla',           label: 'SLA Policies',   icon: Clock },
    { id: 'auto-assign',   label: 'Auto-Assign',    icon: Zap },
    { id: 'members',       label: 'Members',        icon: Users },
    { id: 'product-lines', label: 'Product Lines',  icon: Layers },
    { id: 'task-templates', label: 'Task Templates', icon: ListChecks },
    { id: 'crm',           label: 'CRM',            icon: Inbox },
    { id: 'ai',            label: 'AI',             icon: Bot },
    { id: 'claude-dispatch', label: 'Claude Dispatch', icon: FolderGit2 },
    { id: 'customer-portal', label: 'Customer Portal', icon: Globe },
    { id: 'email',         label: 'Email',          icon: Mail },
    { id: 'integrations',  label: 'Integrations',   icon: KeyRound },
  ];

  if (!currentCompany) {
    return <div className="card p-6 text-center text-muted">Select a company to manage settings.</div>;
  }

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Company Settings"
        intro="Configure your company — general info, ticket classification, categories, teams, SLAs, auto-assign rules, members, product lines, AI, the customer portal, and integrations. Pick a tab below."
      />

      <div className="flex gap-4 border-b border-line overflow-x-auto">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── General ── */}
      {activeTab === 'general' && (
        <div className="card p-6 space-y-6">
          {/* Company name */}
          <div className="space-y-3">
            <div>
              <label className="label">Company name</label>
              <input className="input max-w-md" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => updateCompanyMutation.mutate({ name: companyName, industry: industry || null })}
              disabled={updateCompanyMutation.isPending}
            >
              Save
            </button>
          </div>

          {/* Industry */}
          <div className="border-t border-line pt-6 space-y-4">
            <div>
              <h3 className="section-title mb-1">Industry</h3>
              <p className="text-sm text-muted">
                Setting an industry lets you apply pre-built ticket categories, fields, and product lines in one click.
              </p>
            </div>
            <div className="max-w-md">
              <label className="label">Industry type</label>
              <select
                className="select"
                value={industry}
                onChange={(e) => { setIndustry(e.target.value); setApplyConfirm(false); }}
              >
                <option value="">— Not set —</option>
                {INDUSTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {industry && (
              <div className="max-w-md rounded-lg border border-primary bg-primary-muted p-4 space-y-3">
                <p className="text-sm text-fg">
                  Applying industry defaults will <strong>add</strong> starter categories, custom field definitions,
                  and product lines for <strong>{INDUSTRY_OPTIONS.find(o => o.value === industry)?.label}</strong>.
                  Your existing data will not be removed.
                </p>
                {!applyConfirm ? (
                  <button
                    className="btn btn-secondary flex items-center gap-2"
                    onClick={() => setApplyConfirm(true)}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Apply industry defaults
                  </button>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted">Are you sure?</span>
                    <button
                      className="btn btn-primary"
                      onClick={() => applyIndustryMutation.mutate(industry)}
                      disabled={applyIndustryMutation.isPending}
                    >
                      {applyIndustryMutation.isPending ? 'Applying…' : 'Yes, apply'}
                    </button>
                    <button className="btn btn-ghost" onClick={() => setApplyConfirm(false)}>Cancel</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="border-t border-line pt-6 space-y-4">
            <div>
              <h3 className="section-title mb-1">Ticket notifications</h3>
              <p className="text-sm text-muted">
                Control who gets emailed when tickets come in. For per-category/team routing
                (e.g. send gate-pass requests to the HOA), use the Auto-Assign tab.
              </p>
            </div>

            <div className="max-w-md">
              <label className="label">Notify these emails on every new ticket</label>
              <textarea
                className="input min-h-[72px] resize-y"
                value={notifyEmails}
                onChange={(e) => setNotifyEmails(e.target.value)}
                placeholder="support@yourco.com, dispatch@yourco.com"
              />
              <p className="text-xs text-muted mt-1">
                Comma- or newline-separated. Leave blank to disable. The assigned agent is always notified.
              </p>
            </div>

            <label className="flex items-center gap-2 cursor-pointer max-w-md">
              <input
                type="checkbox"
                className="w-4 h-4 accent-primary"
                checked={notifySubmitter}
                onChange={(e) => setNotifySubmitter(e.target.checked)}
              />
              <span className="text-sm text-fg">
                Send a confirmation copy to staff who submit a ticket
              </span>
            </label>

            <div className="max-w-md">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary"
                  checked={emailIncludeSubject}
                  onChange={(e) => setEmailIncludeSubject(e.target.checked)}
                />
                <span className="text-sm text-fg">
                  Include the ticket subject in email notifications
                </span>
              </label>
              <p className="text-xs text-muted mt-1 ml-6">
                Off by default. Emails are delivered by a third-party provider
                without a BAA — leave this off if tickets may contain PHI or
                other regulated data. When off, emails carry only the ticket
                number, status, priority and a link.
              </p>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => saveNotificationsMutation.mutate()}
              disabled={saveNotificationsMutation.isPending}
            >
              {saveNotificationsMutation.isPending ? 'Saving…' : 'Save notification settings'}
            </button>
          </div>

          {/* Ticket numbering */}
          <div className="border-t border-line pt-6 space-y-4">
            <div>
              <h3 className="section-title mb-1">Ticket numbering</h3>
              <p className="text-sm text-muted">
                Set a short prefix for new ticket numbers. Existing tickets keep their
                current numbers; only tickets created from now on use the new prefix.
              </p>
            </div>

            <div className="max-w-md">
              <label className="label">Ticket prefix</label>
              <input
                className="input"
                value={ticketPrefix}
                onChange={(e) => setTicketPrefix(e.target.value)}
                placeholder="cTKT"
                maxLength={8}
              />
              <p className="text-xs text-muted mt-1">
                1–8 letters or digits. Leave blank to use the default <span className="font-mono">cTKT</span>.
                {' '}New tickets will look like <span className="font-mono text-fg">{prefixPreview}</span>.
              </p>
              {!prefixValid && (
                <p className="text-xs text-err mt-1">Use only 1–8 letters or digits.</p>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={() => saveTicketPrefixMutation.mutate()}
              disabled={saveTicketPrefixMutation.isPending || !prefixValid}
            >
              {saveTicketPrefixMutation.isPending ? 'Saving…' : 'Save ticket prefix'}
            </button>
          </div>
        </div>
      )}

      {/* ── Ticket Statuses ── */}
      {activeTab === 'statuses' && <TicketStatusesTab />}

      {activeTab === 'classification' && <ClassificationsTab />}

      {/* ── CRM: mailboxes, pipeline, lead form ── */}
      {activeTab === 'crm' && <CrmSettingsTab />}

      {/* ── Categories ── */}
      {activeTab === 'categories' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Categories</h2>
            <button className="btn btn-primary inline-flex items-center gap-2" onClick={() => setCategoryModalOpen(true)}>
              <Plus className="w-4 h-4" />Add
            </button>
          </div>
          {(categories as Category[]).length === 0 ? (
            <EmptyState icon={<Tag className="w-6 h-6" />}>
              No categories yet. Click <strong>Add</strong> to create your first ticket category.
            </EmptyState>
          ) : (
            <div className="space-y-2">
              {(categories as Category[]).map((c) => (
                <div key={(c as any).id ?? (c as any)._id} className="flex items-center justify-between py-2 border-b border-line-light">
                  <span className="flex items-center gap-2 text-fg">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#94a3b8' }} />
                    {c.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Teams ── */}
      {activeTab === 'teams' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Teams</h2>
            <button className="btn btn-primary inline-flex items-center gap-2" onClick={() => setTeamModalOpen(true)}>
              <Plus className="w-4 h-4" />Add
            </button>
          </div>
          {(teams as Team[]).length === 0 ? (
            <EmptyState icon={<Users className="w-6 h-6" />}>
              No teams yet. Click <strong>Add</strong> to group technicians into a team.
            </EmptyState>
          ) : (
            <div className="space-y-2">
              {(teams as Team[]).map((t) => {
                const memberNames = (t.members ?? [])
                  .map((m) => m.user?.name ?? m.user?.email)
                  .filter(Boolean) as string[];
                return (
                  <button
                    key={(t as any).id ?? (t as any)._id}
                    type="button"
                    onClick={() => setEditingTeam(t)}
                    className="w-full flex items-start justify-between gap-3 py-2.5 border-b border-line-light text-left hover:bg-row-odd rounded-md px-2 -mx-2 transition-colors"
                    title="Edit team and members"
                  >
                    <span>
                      <span className="block text-fg font-medium">{t.name}</span>
                      <span className="block text-xs text-muted mt-0.5">
                        {memberNames.length > 0 ? memberNames.join(', ') : 'No members yet — click to add'}
                      </span>
                    </span>
                    <span className="text-sm text-muted shrink-0">
                      {t._count?.members ?? memberNames.length} member{(t._count?.members ?? memberNames.length) === 1 ? '' : 's'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-muted mt-3">
            A person can belong to more than one team. Everyone on a team is
            emailed when a ticket is assigned to it.
          </p>
        </div>
      )}

      {/* ── SLA ── */}
      {activeTab === 'sla' && <SLAPoliciesTab />}

      {/* ── Auto-Assign ── */}
      {activeTab === 'auto-assign' && <AutoAssignTab />}

      {/* ── Members ── */}
      {activeTab === 'members' && <MembersTab />}

      {/* ── Product Lines ── */}
      {activeTab === 'product-lines' && <ProductLinesTab />}

      {activeTab === 'task-templates' && <TaskTemplatesTab />}

      {/* ── AI ── */}
      {activeTab === 'ai' && (() => {
        const providerMeta = AI_PROVIDERS.find(p => p.value === aiProvider);
        return (
          <div className="card p-6 space-y-6 max-w-2xl">
            <div>
              <h2 className="section-title">AI Configuration</h2>
              <p className="text-sm text-muted mt-1">
                Configure an AI provider for ticket summarization, reply suggestions, sentiment detection, and more.
                Each company can use its own API key and model.
              </p>
            </div>

            {/* Current status */}
            {aiSettings && (
              <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                aiSettings.aiProvider && aiSettings.hasApiKey
                  ? 'border-ok text-ok'
                  : 'border-line bg-row-odd text-muted'
              }`}>
                <Bot className="w-4 h-4 shrink-0" />
                {aiSettings.aiProvider && aiSettings.hasApiKey
                  ? `Active: ${AI_PROVIDERS.find(p => p.value === aiSettings.aiProvider)?.label ?? aiSettings.aiProvider}${aiSettings.aiModel ? ` · ${aiSettings.aiModel}` : ''}`
                  : 'Not configured — using mock AI responses'}
              </div>
            )}

            {/* Provider select */}
            <div className="space-y-4">
              <div>
                <label className="label">Provider</label>
                <select
                  className="select max-w-xs"
                  value={aiProvider}
                  onChange={e => { setAiProvider(e.target.value); setAiModel(''); }}
                >
                  <option value="">— Select provider —</option>
                  {AI_PROVIDERS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {providerMeta && (
                <>
                  <div>
                    <label className="label">Model</label>
                    <select
                      className="select max-w-xs"
                      value={aiModel}
                      onChange={e => setAiModel(e.target.value)}
                    >
                      <option value="">— Default —</option>
                      {providerMeta.models.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">
                      API Key
                      {aiSettings?.hasApiKey && (
                        <span className="ml-2 text-xs font-normal text-ok">
                          ✓ Key saved — enter a new value to replace it
                        </span>
                      )}
                    </label>
                    <div className="relative max-w-md">
                      <input
                        className="input pr-10"
                        type={showKey ? 'text' : 'password'}
                        value={aiApiKey}
                        onChange={e => setAiApiKey(e.target.value)}
                        placeholder={aiSettings?.hasApiKey ? '••••••••••••••••' : providerMeta.keyPlaceholder}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
                        onClick={() => setShowKey(v => !v)}
                      >
                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted mt-1">{providerMeta.keyHint}</p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                className="btn btn-primary"
                disabled={!aiProvider || saveAISettingsMutation.isPending}
                onClick={() => {
                  const payload: any = { aiProvider, aiModel: aiModel || '' };
                  if (aiApiKey) payload.aiApiKey = aiApiKey;
                  saveAISettingsMutation.mutate(payload);
                }}
              >
                {saveAISettingsMutation.isPending ? 'Saving…' : 'Save AI settings'}
              </button>
              {(aiSettings?.aiProvider || aiSettings?.hasApiKey) && (
                <button
                  className="btn btn-ghost text-err"
                  disabled={saveAISettingsMutation.isPending}
                  onClick={() => saveAISettingsMutation.mutate({ aiProvider: '', aiApiKey: '', aiModel: '' })}
                >
                  Clear / disable AI
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Customer Portal ── */}
      {activeTab === 'claude-dispatch' && <ClaudeDispatchTab />}

      {activeTab === 'customer-portal' && <PortalSettingsTab />}

      {activeTab === 'email' && <EmailTab />}

      {activeTab === 'integrations' && <ApiKeysTab />}

      {/* ── Modals ── */}
      {categoryModalOpen && (
        <NameModal
          title="New Category"
          label="Category name"
          placeholder="e.g. Billing"
          onSubmit={(name) => createCategoryMutation.mutate(name)}
          onClose={() => setCategoryModalOpen(false)}
        />
      )}

      {teamModalOpen && (
        <TeamEditorModal team={null} onClose={() => setTeamModalOpen(false)} />
      )}
      {editingTeam && (
        <TeamEditorModal team={editingTeam} onClose={() => setEditingTeam(null)} />
      )}
    </div>
  );
}
